// src/components/accueil/CompteursFile.tsx — SCE Issues
// Les quatre compteurs du coordinateur (Lot 9). Chacun est aussi un onglet :
// un clic choisit la file affichée dessous (colonne 1 au bureau, liste sous
// les onglets au téléphone). Au bureau, le survol détaille (comme les KPI des
// statistiques).
//
// Tuile choisie (28/09, piste C de la maquette) : carte blanche soulevée, les
// autres à plat — et non plus remplie de jaune, qui écrasait le repère de
// couleur et pesait plus lourd que la file elle-même. Styles dans globals.css
// (`.compteur-file`) pour le survol.
//
// Seuil d'alerte (chantier du 03/11) : une file qui dépasse le sien porte un
// point rouge en haut à droite, et son infobulle dit pourquoi.

import { Infobulle } from '@/components/ui/Infobulle'
import type { IdFile } from '@/utils/accueil'

/** Ce qu'il faut d'une file pour son compteur : les quatre du code, ou une file composée (lot 5). */
export interface FileCompteur {
  id: string
  libelle: string
  court: string
  issues: readonly unknown[]
  precision: string
  infobulle: { titre: string; lignes: string[] }
}

// Repère de couleur de chaque file. « À qualifier » hachuré, comme dans les
// barres des statistiques : pas encore de criticité, donc pas de couleur. Une
// file composée : gris moyen.
const TEINTES: Record<IdFile, string> = {
  aQualifier: 'repeating-linear-gradient(135deg, var(--color-gray-60) 0 2px, var(--color-white) 2px 5px)',
  critiques: 'var(--color-traffic-red)',
  sansActivite: 'var(--color-gray-80)',
  enCours: 'var(--color-gray-90)',
}

export function CompteursFile({
  files,
  choisi,
  onChoisir,
  variante,
  enAttente = [],
  alertes,
  nouvelles,
}: {
  files: readonly FileCompteur[]
  choisi: string
  onChoisir: (id: string) => void
  variante: 'tuiles' | 'onglets'
  /** Files dont le chiffre n'est pas encore sûr : « — » à la place. */
  enAttente?: readonly string[]
  /** Files au-delà de leur seuil, avec le seuil en clair. */
  alertes?: ReadonlyMap<string, string>
  /** Files arrivées du modèle de /admin : « Nouveau » le temps de la visite. */
  nouvelles?: ReadonlySet<string>
}): JSX.Element {
  const tuiles = variante === 'tuiles'
  return (
    <div
      role="tablist"
      aria-label="File de travail"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${files.length}, minmax(0, 1fr))`,
        gap: tuiles ? 12 : 6,
      }}
    >
      {files.map((f) => {
        const actif = f.id === choisi
        const rouge = f.id === 'critiques' && f.issues.length > 0
        const alerte = enAttente.includes(f.id) ? undefined : alertes?.get(f.id)
        const bouton = (
          <button
            type="button"
            role="tab"
            aria-selected={actif}
            onClick={() => onChoisir(f.id)}
            className="compteur-file w-full"
            style={{
              boxSizing: 'border-box',
              borderRadius: 12,
              color: 'var(--color-text-primary)',
              fontFamily: 'inherit',
              cursor: 'pointer',
              ...(tuiles
                ? { height: 72, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left' }
                : { minHeight: 60, padding: '4px 2px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }),
            }}
          >
            {nouvelles?.has(f.id) && <span className="carte-nouvelle carte-nouvelle--compteur">Nouveau</span>}
            {alerte !== undefined && <span className="compteur-alerte" role="img" aria-label={`Alerte : ${alerte.toLowerCase()}`} />}
            {tuiles && (
              <span aria-hidden="true" style={{ width: 6, height: 44, borderRadius: 3, flexShrink: 0, background: (TEINTES as Record<string, string>)[f.id] ?? 'var(--color-gray-50)' }} />
            )}
            <span
              style={{
                fontSize: tuiles ? 30 : 22,
                lineHeight: tuiles ? '34px' : '24px',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
                minWidth: tuiles ? 34 : undefined,
                color: rouge ? 'var(--color-traffic-red)' : 'var(--color-text-primary)',
              }}
            >
              {enAttente.includes(f.id) ? '—' : f.issues.length}
            </span>
            {tuiles ? (
              <span className="flex flex-col min-w-0" style={{ flexGrow: 1 }}>
                <span style={{ fontSize: 15, fontWeight: 500 }}>{f.libelle}</span>
                <span
                  style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {f.precision}
                </span>
              </span>
            ) : (
              <span style={{ fontSize: 11, lineHeight: '13px', textAlign: 'center', fontWeight: actif ? 500 : 400 }}>
                {f.court}
              </span>
            )}
          </button>
        )
        return tuiles ? (
          <Infobulle
            key={f.id}
            titre={f.infobulle.titre}
            lignes={alerte !== undefined ? [`Alerte : ${alerte.toLowerCase()}`, ...f.infobulle.lignes] : f.infobulle.lignes}
            largeur={360}
            style={{ display: 'block' }}
          >
            {bouton}
          </Infobulle>
        ) : (
          <div key={f.id}>{bouton}</div>
        )
      })}
    </div>
  )
}
